import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import GroundStateBadge from './GroundStateBadge';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FlagIcon, AirlineLogo, AircraftTypeIcon, formatTime, formatDurationMinutes } from './AircraftTableComponents';
import { isGroundVehicle } from '../utils/groundVehicle';
import { getAircraftOnGround, getGroundTrackPoints } from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';
import { rowKey, departureOrder, remainingTaxiMinutes, groundCounts } from '../utils/departureRows';
import useDepartureRows from '../hooks/useDepartureRows';
import { isRecentlyActive, isActiveOnGround, isOnGroundActive, groupOnGroundRows } from '../utils/onGroundActivity';
import { onGroundTrackQuery } from '../utils/landingTrack';
import useLandingTrack from '../hooks/useLandingTrack';
import { LandingTrackLine, LandingTrackPopup } from './LandingTrack';
import GroundCounters from './GroundCounters';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { getAirlineLogo, hasAirlineLogo, getMilitaryLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import { getColourSettings, getAircraftColourFilter } from '../utils/mapStyles';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';

// Snap heading to nearest 5 degrees. Surface heading from the
// backend gets re-estimated every poll, so an actively-taxiing
// aircraft would otherwise show heading deltas like 273° → 274° →
// 275°, each of which invalidates the marker's memo equality and
// forces react-leaflet to tear down + recreate the DOM element —
// visible as the marker "appearing and disappearing". Snapping
// keeps the equality stable for small motion.
function snapHeading(h) {
    if (h == null) return 0;
    return Math.round(h / 5) * 5;
}

/**
 * Build a leaflet divIcon that uses the same beluga sprite sheet as
 * the Live / History / Predictions maps. Mirrors the icon-building
 * code in AircraftMarker so the on-ground page renders aircraft with
 * the SAME visual style users see everywhere else.
 *
 * @param aircraftType  ICAO type designator (e.g. "B738") for sprite lookup.
 * @param heading       Rotation in degrees.
 * @param ghostMode     true → grey + 50% opacity (parked / inactive),
 *                      false → normal yellow (active).
 */
function buildSpriteIcon(aircraftType, heading, ghostMode, isSelected) {
    const spritePos = getAircraftSpritePosition(aircraftType);
    const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
    // Inactive aircraft (parked, no recent surface msg) render at 70%
    // size per user direction so the active aircraft visually dominate
    // the apron view.
    const scaleFactor = (ghostMode && !isSelected) ? 0.7 : 1.0;
    const iconSize = Math.round(32 * sizeMultiplier * scaleFactor);
    const halfSize = iconSize / 2;
    const bgPosX = -(spritePos.col * iconSize);
    const bgPosY = -(spritePos.row * iconSize);

    const colours = getColourSettings();
    const normalFilter = getAircraftColourFilter(colours.aircraftNormal);
    const selectedFilter = getAircraftColourFilter(colours.aircraftSelected || '#FF6B35');
    const militaryFilter = getAircraftColourFilter(colours.aircraftMilitary || '#C0C0C0');

    const baseFilter = isSelected
        ? selectedFilter.filter
        : ghostMode
            ? militaryFilter.filter
            : normalFilter.filter;
    const filter = `${baseFilter}
           drop-shadow(0.5px 0px 0px ${isSelected ? '#000' : '#333'})
           drop-shadow(-0.5px 0px 0px ${isSelected ? '#000' : '#333'})
           drop-shadow(0px 0.5px 0px ${isSelected ? '#000' : '#333'})
           drop-shadow(0px -0.5px 0px ${isSelected ? '#000' : '#333'})`;

    const spriteStyle = `width:${iconSize}px; height:${iconSize}px;
        background-size:${iconSize * 8}px auto;
        background-position:${bgPosX}px ${bgPosY}px;
        background-image:url(${spriteImage});
        background-repeat:no-repeat;
        display:inline-block;`;

    const wrapperStyle = `position:relative; width:${iconSize}px; height:${iconSize}px;${ghostMode && !isSelected ? ' opacity:0.5;' : ''}`;
    // Label is no longer baked into the divIcon — we use a permanent
    // leaflet Tooltip with class "aircraft-tooltip" so the style
    // matches every other map (Live / History / Predictions /
    // Departures) which all share that CSS class.
    return L.divIcon({
        html: `<div style="${wrapperStyle}">
                 <div style="position:absolute; top:0; left:0;
                             transform:rotate(${heading || 0}deg); transform-origin:center center;
                             width:${iconSize}px; height:${iconSize}px;">
                   <div style="${spriteStyle} filter:${filter};"></div>
                 </div>
               </div>`,
        iconSize: [iconSize, iconSize],
        iconAnchor: [halfSize, halfSize],
        className: ghostMode ? 'aircraft-marker aircraft-marker-ghost' : 'aircraft-marker'
    });
}

/**
 * Popup-style hover tooltip for an on-ground aircraft. Same visual
 * language as AircraftInfoPopup but compact for a leaflet Tooltip.
 * Includes the landing time and on-ground duration so operators can
 * tell at a glance how long the aircraft has been at the stand.
 */
function HoverPopup({ a }) {
    useAirlineLogoMapVersion();
    const isMilitary = a.military === true;
    let logoSrc = null;
    if (isMilitary && a.countryCode && hasMilitaryLogo(a.countryCode)) {
        logoSrc = getMilitaryLogo(a.countryCode, a.airline || a.ownerOperator);
    } else if (a.airlineCode && hasAirlineLogo(a.airlineCode)) {
        logoSrc = getAirlineLogo(a.airlineCode, 'icon');
    } else if (a.airlineIcao && hasAirlineLogo(a.airlineIcao)) {
        logoSrc = getAirlineLogo(a.airlineIcao, 'icon');
    }
    return (
        <div style={{ minWidth: 220, maxWidth: 280, padding: '2px 0', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif' }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                paddingBottom: 6, marginBottom: 6,
                borderBottom: '2px solid #FF6B35'
            }}>
                {logoSrc && (
                    <img src={logoSrc} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }}
                         onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                        {a.callsign || a.icaoAddress}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.75, lineHeight: 1.2 }}>
                        {a.registration || a.icaoAddress}
                        {a.aircraftType ? ` · ${a.aircraftType}` : ''}
                    </div>
                </div>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                {a.aircraftDescription && (
                    <div style={{ opacity: 0.85 }}>{a.aircraftDescription}</div>
                )}
                {a.country && (
                    <div><strong>Country:</strong> {a.country}</div>
                )}
                {a.iataFlightNumber && (
                    <div><strong>Flight:</strong> {a.iataFlightNumber}</div>
                )}
                {a.assignedGate && (
                    <div><strong>Gate:</strong> {a.assignedGate}</div>
                )}
                {a.groundState && (
                    <div><strong>State:</strong> {a.groundState}</div>
                )}
                {a.arrivedAt && (
                    <div><strong>Landed:</strong> {a.landingConfidence != null && a.landingConfidence < 100 ? '~' : ''}{formatTime(a.arrivedAt)}
                        {a.landingConfidence != null && a.landingConfidence < 100 ? ` (${a.landingConfidence} %)` : ''}</div>
                )}
                {a.timeOnGroundMinutes != null && (
                    <div><strong>On ground:</strong> {formatDurationMinutes(a.timeOnGroundMinutes)}</div>
                )}
            </div>
        </div>
    );
}

/**
 * Per-aircraft marker. Memoised so the leaflet divIcon is only
 * rebuilt when its visual inputs change — heading, active/ghost
 * state, selected state, callsign. Without this, react-leaflet
 * tears down and recreates the marker DOM on every poll (every 5s),
 * which the user saw as aircraft "showing then disappearing then
 * showing again". Position changes still re-render via the Marker's
 * own internal optimisation.
 */
const OnGroundMarker = React.memo(function OnGroundMarker({
    aircraft, isSelected, active, onSelect, depOrder
}) {
    useAirlineLogoMapVersion();
    const a = aircraft;
    const snappedHeading = snapHeading(a.heading);
    const icon = useMemo(
        () => buildSpriteIcon(
            a.aircraftType,
            snappedHeading,
            !active && !isSelected,
            isSelected
        ),
        // Only rebuild when the things the icon actually depends on
        // change. Uses snappedHeading so small per-poll heading
        // deltas don't trigger an icon rebuild.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [a.aircraftType, snappedHeading, active, isSelected]
    );

    // Permanent label — same .aircraft-tooltip styling the Live /
    // History / Predictions / Departures maps use, so it visually
    // matches everywhere. Latest known callsign for the airframe
    // (inbound flight number until a fresh outbound callsign is
    // transmitted, automatically).
    const isMilitary = a.military === true;
    let logoSrc = null;
    if (isMilitary && a.countryCode && hasMilitaryLogo(a.countryCode)) {
        logoSrc = getMilitaryLogo(a.countryCode, a.airline || a.ownerOperator);
    } else {
        const airlineCode = a.airlineCode || a.airlineIcao;
        if (airlineCode && hasAirlineLogo(airlineCode)) {
            logoSrc = getAirlineLogo(airlineCode, 'icon');
        }
    }
    const labelText = a.callsign || a.iataFlightNumber || a.icaoAddress;

    return (
        <Marker
            position={[a.currentLat, a.currentLon]}
            icon={icon}
            eventHandlers={{ click: () => onSelect(a.icaoAddress) }}
        >
            <Tooltip permanent direction="top" offset={[0, -14]} className="aircraft-tooltip">
                <div className="tooltip-content">
                    {/* Predicted departure order, as on the Departures map and the Dep # column. */}
                    {depOrder != null && (
                        <span className="tooltip-seq" title="Predicted departure order">{depOrder}</span>
                    )}
                    {logoSrc && (
                        <img
                            src={logoSrc}
                            alt={isMilitary ? 'Military' : (a.airlineCode || '')}
                            className="tooltip-airline-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <span className="tooltip-label">{labelText}</span>
                </div>
            </Tooltip>
        </Marker>
    );
}, (prev, next) => {
    // Equality on ONLY the fields that actually drive what the
    // marker renders. Per-poll heading wiggle is squashed via
    // snapHeading() so taxi motion in the 0–5° range doesn't bounce
    // the marker. Speed, ages, gate strings, state — none of those
    // affect the icon or label, so they're skipped.
    const a = prev.aircraft, b = next.aircraft;
    return a.icaoAddress === b.icaoAddress
        && a.currentLat === b.currentLat
        && a.currentLon === b.currentLon
        && snapHeading(a.heading) === snapHeading(b.heading)
        && a.callsign === b.callsign
        && a.iataFlightNumber === b.iataFlightNumber
        && a.aircraftType === b.aircraftType
        && a.airlineCode === b.airlineCode
        && a.airlineIcao === b.airlineIcao
        && a.military === b.military
        && a.countryCode === b.countryCode
        && prev.active === next.active
        && prev.isSelected === next.isSelected
        && prev.depOrder === next.depOrder;
});

// Airport center coordinates for the on-ground map.
const AIRPORT_CENTER = {
    EIDW: [53.4213, -6.2701],
    LPPR: [41.2481, -8.6814]
};

// Airport-boundary bounding boxes, [southLat, westLon, northLat, eastLon].
// Used to lock the map zoom on the airport itself rather than fit-to-
// aircraft, so the camera stays at airport scale regardless of where
// individual aircraft happen to be reporting. Coordinates encompass
// runways + main apron at each field with a small margin.
const AIRPORT_BOUNDS = {
    EIDW: [[53.408, -6.293], [53.438, -6.232]],
    LPPR: [[41.229, -8.692], [41.260, -8.663]]
};

// Max distance (km) an aircraft's reported position can be from the
// airport center before we treat the position as stale-airborne and
// drop it from the map. The on-ground endpoint sometimes falls back
// to airport_movement.latitude/longitude — which is the FIRST seen
// inbound position, often hundreds of km out. Without this filter
// the map shows parked aircraft scattered across UK + Europe.
const MAP_PLOT_RADIUS_KM = 8;

function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Lock the map view to the airport boundary. Fires once per airport
 * change (not per aircraft update) so the camera stays anchored on
 * the airfield regardless of where individual rows say they are.
 */
function LockToAirportBoundary({ airportBounds, airportCenter }) {
    const map = useMap();
    useEffect(() => {
        if (airportBounds) {
            try { map.fitBounds(airportBounds, { padding: [10, 10] }); return; }
            catch (_) { /* fall through to centre */ }
        }
        if (airportCenter) map.setView(airportCenter, 14);
    }, [airportBounds, airportCenter, map]);
    return null;
}


// State pill: shared with Departures (GroundStateBadge, --ground-* theme variables).

/**
 * On-ground panel for the merged Predictions page.
 *
 * Lives next to Arrivals + Departures tabs and polls
 * AirportViewService.getAircraftOnGround every 5 s while visible —
 * same cadence Airport.js used. The data shape comes from the
 * existing backend endpoint (no backend change needed for this slice).
 */
export default function PredictionsOnGround({ selectedAirport, selectedReceiverUuid, onJumpToHistory, departureForecastUpdate, liveAircraft }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Selected aircraft (icao hex). Click on a map marker or table row
    // to select; click again to deselect. Selected aircraft is rendered
    // in highlight colour and its last-arrived ground track is fetched
    // + drawn on the map.
    const [selectedHex, setSelectedHex] = useState(null);
    const [selectedTrackPoints, setSelectedTrackPoints] = useState([]);
    // Coarse "now" tick (refreshes every 30 s) drives the 5-min
    // recently-active threshold. Without this, every parent render
    // would re-evaluate Date.now() against lastUpdateAt and could
    // flip the active flag on any of the markers — that flag controls
    // the icon's ghost mode, which would force the icon to rebuild
    // and the marker to flicker.
    const [nowTickMs, setNowTickMs] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTickMs(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    // Stable click handler — passed to OnGroundMarker so its
    // React.memo equality check holds across parent re-renders.
    const handleMarkerSelect = useCallback((hex) => {
        setSelectedHex(prev => prev === hex ? null : hex);
    }, []);
    // Default sort: most-recent arrival first. The user clarified after
    // the first attempt that "most recent first" is the desired view —
    // operators want the freshest movement at the top so it doesn't
    // need to be scrolled to.
    const [sortConfig, setSortConfig] = useState({ key: 'arrivedAt', direction: 'desc' });
    const pollRef = useRef(null);

    // Monotonic sequence so a slow fetch can't overwrite a newer
    // fetch's data on completion. Each call increments and remembers
    // its own sequence; if the global counter has advanced when the
    // response lands, we discard the result. Without this, two
    // overlapping polls can deliver responses out of order and the
    // older snapshot — missing whichever aircraft has just moved —
    // wins, causing the markers to flicker.
    const fetchSeqRef = useRef(0);

    const fetchOnce = useCallback(async () => {
        if (!selectedAirport || !selectedReceiverUuid) return;
        const token = getAccessToken();
        if (!token) return;
        const mySeq = ++fetchSeqRef.current;
        try {
            const resp = await getAircraftOnGround(selectedAirport, selectedReceiverUuid, token);
            if (mySeq !== fetchSeqRef.current) return; // newer fetch already landed; discard
            const ok = resp?.header?.apiStatus === 'OK' || resp?.header?.apiStatus === 'SUCCESS';
            if (ok) {
                // Ground vehicles are excluded server-side; the callsign heuristic catches one the classifier hasn't flagged yet.
                setData((resp.aircraft || []).filter(a => !isGroundVehicle(a)));
                setError(null);
            } else {
                setError('Failed to load on-ground aircraft');
            }
        } catch (err) {
            if (mySeq !== fetchSeqRef.current) return;
            setError('On-ground data unavailable');
        }
    }, [selectedAirport, selectedReceiverUuid]);

    // Initial load + 5-sec polling. Stops cleanly on unmount or when
    // the receiver/airport changes.
    useEffect(() => {
        if (!selectedAirport || !selectedReceiverUuid) return undefined;
        setLoading(true);
        fetchOnce().finally(() => setLoading(false));
        pollRef.current = setInterval(fetchOnce, 5000);
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [selectedAirport, selectedReceiverUuid, fetchOnce]);

    // Fetch the last-arrived ground track for the selected aircraft.
    // We look up the row to grab its groundOperationId (set by the
    // backend for the latest landing) and call
    // AirportViewService.getGroundTrackPoints. Clear immediately on
    // de-select so the polyline doesn't linger.
    useEffect(() => {
        if (!selectedHex) {
            setSelectedTrackPoints([]);
            return undefined;
        }
        const row = data.find(a => a.icaoAddress === selectedHex);
        if (!row || row.groundOperationId == null) {
            setSelectedTrackPoints([]);
            return undefined;
        }
        const token = getAccessToken();
        if (!token) return undefined;
        let cancelled = false;
        getGroundTrackPoints(row.groundOperationId, false, false, token)
            .then(resp => {
                if (cancelled) return;
                const ok = resp?.header?.apiStatus === 'OK' || resp?.header?.apiStatus === 'SUCCESS';
                if (!ok) { setSelectedTrackPoints([]); return; }
                const pts = (resp.trackPoints || resp.points || [])
                    .filter(p => p.latitude != null && p.longitude != null)
                    .map(p => [p.latitude, p.longitude]);
                setSelectedTrackPoints(pts);
            })
            .catch(() => { if (!cancelled) setSelectedTrackPoints([]); });
        return () => { cancelled = true; };
    }, [selectedHex, data]);

    // The selected row's aircraft: the Live page's popup and its landing + ground track from 2 km
    // before touchdown (10 min before arrivedAt onwards), opened by the same row / marker click.
    // The ground-operation track above stays as the fallback when no ADS-B positions are left.
    const selectedRow = useMemo(
        () => (selectedHex ? data.find(a => a.icaoAddress === selectedHex) : null),
        [data, selectedHex]);
    const landingTrack = useLandingTrack(useMemo(() => onGroundTrackQuery(selectedRow), [selectedRow]));

    const handleSort = (key) => {
        setSortConfig(prev => prev.key === key
            ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: 'asc' });
    };

    const sorted = useMemo(() => {
        const arr = [...data];
        const { key, direction } = sortConfig;
        const dir = direction === 'asc' ? 1 : -1;
        const tickMs = nowTickMs; // Snapshot for stable activity-bucket sort.
        // Composite ordering per user direction:
        //   "The top of the table should be active aircraft
        //    (arriving departing) and below, inactive aircraft.
        //    Both ordered by arrival time desc."
        // Primary key:   active (yes/no) — recently-active OR
        //                state-active.
        // Secondary key: user-clicked sort (default arrivedAt desc).
        // Tie-breaker:   icaoAddress for deterministic ordering
        //                across polls.
        const activityRank = (a) => (isOnGroundActive(a, tickMs) ? 0 : 1);
        arr.sort((a, b) => {
            const ra = activityRank(a);
            const rb = activityRank(b);
            if (ra !== rb) return ra - rb;
            const av = a?.[key];
            const bv = b?.[key];
            if (av == null && bv == null) {
                // fall through to tiebreaker
            } else if (av == null) return 1;
            else if (bv == null) return -1;
            else if (av < bv) return -1 * dir;
            else if (av > bv) return 1 * dir;
            const ax = a?.icaoAddress || '';
            const bx = b?.icaoAddress || '';
            return ax < bx ? -1 : ax > bx ? 1 : 0;
        });
        return arr;
    }, [data, sortConfig, nowTickMs]);

    // Active / Parked groups for the table's dividers, as the arrivals table's In Flight / Landed.
    // The sort above already ranks active first by the same predicate, so the groups keep its order.
    const groups = useMemo(() => groupOnGroundRows(sorted, nowTickMs), [sorted, nowTickMs]);
    // Collapse the (often large) Parked group so moving aircraft stay front-and-centre.
    const [parkedCollapsed, setParkedCollapsed] = useState(false);

    // Hooks must run on every render, so derive these BEFORE any early
    // return. (Hooks-after-return triggers an eslint error and breaks
    // render order under StrictMode.)
    const mapCenter = AIRPORT_CENTER[selectedAirport] || AIRPORT_CENTER.EIDW;

    // Outbound aircraft: their place in the predicted departure queue and taxi time left, from the
    // same departure sessions the Departures tab shows. Keyed by hex; an airframe has at most one
    // active session.
    const today = new Date().toISOString().split('T')[0];
    const { rows: departureRows } = useDepartureRows(selectedAirport, today, departureForecastUpdate);
    const departureByHex = useMemo(() => {
        const order = departureOrder(departureRows, nowTickMs);
        const out = new Map();
        for (const r of departureRows) {
            const pos = order.get(rowKey(r.icaoAddress, r.callsign, r.sessionStartAt));
            if (pos == null || !r.icaoAddress) continue;
            out.set(r.icaoAddress.toUpperCase(), { pos, remainingMin: remainingTaxiMinutes(r, nowTickMs) });
        }
        return out;
    }, [departureRows, nowTickMs]);
    const counters = useMemo(() => groundCounts({ onGround: data }), [data]);
    const airportBounds = AIRPORT_BOUNDS[selectedAirport] || AIRPORT_BOUNDS.EIDW;

    // Only plot aircraft we're CONFIDENT are actually on the apron.
    // Four routes to confidence, in order:
    //   1. Backend snapped the position to an OSM stand AND set
    //      hasGroundPosition=true. This is authoritative — the
    //      coordinates ARE the gate, regardless of state.
    //   2. Recently active — we got a surface message within the
    //      last minute (RECENT_ACTIVE_MS; matches the live map's
    //      eviction time and the backend's ground-state CASE).
    //   3. Backend gave us an assigned gate (surface tracking
    //      confirmed) — position is from real ADS-B.
    //   4. State says active. For parked aircraft with NO gate and
    //      NO recent message, the last position is almost always a
    //      stale runway/taxiway fix — those get dropped per "we
    //      should not display those locations after aircraft status
    //      changes to parked".
    const mapAircraft = useMemo(() => {
        const center = mapCenter;
        const nowMs = Date.now();
        return data.filter(a => {
            if (a.currentLat == null || a.currentLon == null) return false;
            const distKm = haversineKm(center[0], center[1], a.currentLat, a.currentLon);
            if (distKm > MAP_PLOT_RADIUS_KM) return false;
            if (a.hasGroundPosition === true) return true;
            if (isRecentlyActive(a.lastUpdateAt, nowMs)) return true;
            if (a.assignedGate) return true;
            return isActiveOnGround(a.groundState);
        });
    }, [data, mapCenter]);

    if (!selectedAirport || !selectedReceiverUuid) {
        return <div className="no-data" style={{ padding: 24 }}>Select an airport + receiver to see on-ground aircraft.</div>;
    }

    return (
        <div className="predictions-onground" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Full-width header bar — count summary above the split. */}
            <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <strong>{data.length} aircraft on the ground</strong>
                <span style={{ opacity: 0.6 }}>
                    {mapAircraft.length} with a known surface position
                    {loading && ' · loading…'}
                </span>
            </div>
            {error && <div className="error-message">{error}</div>}
            {/* Body: map + table. Side-by-side on desktop, stacked on mobile
                (see .onground-split in Predictions.css — inline flex can't be
                overridden by media queries, so the layout lives in CSS). */}
            <div className="onground-split">
            {/* Left/top: parking-position map. */}
            <div className="onground-map">
                <MapContainer
                    center={mapCenter}
                    zoom={14}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                >
                    <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    <LockToAirportBoundary airportBounds={airportBounds} airportCenter={mapCenter} />
                    {mapAircraft.map(a => {
                        // Treat as active (yellow) for 1 min after the
                        // last surface message, regardless of state.
                        // After that, only state-derived activity (a
                        // fresh pushback we just heard about) keeps it
                        // yellow; everything else greys out.
                        const active = isOnGroundActive(a, nowTickMs);
                        return (
                            <OnGroundMarker
                                key={a.icaoAddress}
                                aircraft={a}
                                active={active}
                                isSelected={selectedHex === a.icaoAddress}
                                depOrder={departureByHex.get((a.icaoAddress || '').toUpperCase())?.pos}
                                onSelect={handleMarkerSelect}
                            />
                        );
                    })}
                    {/* Landing + ground track for the selected aircraft; the ground-operation
                        track when no ADS-B positions came back. */}
                    <LandingTrackLine positions={landingTrack.positions} />
                    {landingTrack.positions.length < 2 && selectedTrackPoints.length >= 2 && (
                        <Polyline
                            positions={selectedTrackPoints}
                            pathOptions={{ color: '#FF6B35', weight: 3, opacity: 0.9 }}
                        />
                    )}
                </MapContainer>
                <GroundCounters items={counters} />
                <LandingTrackPopup hex={selectedHex} track={landingTrack} liveAircraft={liveAircraft} />
            </div>
            {/* Right: scrollable list. .predictions-table-container
                gives it the same look as the Predictions arrivals
                table. */}
            <div className="predictions-table-container" style={{ flex: 1, minHeight: 0 }}>
            <table className="predictions-table">
                <thead>
                    <tr>
                        <th>Flag</th>
                        <th onClick={() => handleSort('icaoAddress')} title="Click hex to view in History">Hex</th>
                        <th onClick={() => handleSort('registration')}>Reg</th>
                        <th>Airline</th>
                        <th onClick={() => handleSort('callsign')}>Callsign</th>
                        <th onClick={() => handleSort('iataFlightNumber')}>Flight</th>
                        <th onClick={() => handleSort('aircraftType')}>Type</th>
                        <th onClick={() => handleSort('groundState')}>State</th>
                        <th className="numeric" title="Predicted departure order: aircraft on the ground numbered by predicted liftoff, 1 = next away">Dep #</th>
                        <th title="Estimated taxi time left: minutes until the predicted liftoff">Taxi Left</th>
                        <th>Gate</th>
                        <th onClick={() => handleSort('arrivedAt')}>Last arrived</th>
                        <th onClick={() => handleSort('lastUpdateAt')} title="Most recent ADS-B surface message we received">Last msg</th>
                        <th onClick={() => handleSort('timeOnGroundMinutes')} className="numeric">On Ground</th>
                    </tr>
                </thead>
                <tbody>
                    {[...groups.active, ...groups.parked].map((a, idx) => {
                        const isSelected = selectedHex === a.icaoAddress;
                        const dep = a.icaoAddress ? departureByHex.get(a.icaoAddress.toUpperCase()) : null;
                        // Active / Parked group dividers, as the arrivals table's In Flight / Landed.
                        const isParked = idx >= groups.active.length;
                        const showActiveHeader = idx === 0 && !isParked;
                        const showParkedHeader = idx === groups.active.length;
                        const rowVisible = !isParked || !parkedCollapsed;
                        return (
                        <React.Fragment key={a.icaoAddress}>
                        {showActiveHeader && (
                            <tr className="prediction-group-divider">
                                <td colSpan={100}><span className="grp-icon">✈</span> Active <span className="grp-count">{groups.active.length}</span></td>
                            </tr>
                        )}
                        {showParkedHeader && (
                            <tr className="prediction-group-divider parked"
                                onClick={(e) => { e.stopPropagation(); setParkedCollapsed(v => !v); }}
                                title={parkedCollapsed ? 'Show parked aircraft' : 'Hide parked aircraft'}>
                                <td colSpan={100}>
                                    <span className="grp-caret">{parkedCollapsed ? '▸' : '▾'}</span>{' '}
                                    <span className="grp-icon">P</span> Parked <span className="grp-count">{groups.parked.length}</span>
                                </td>
                            </tr>
                        )}
                        {rowVisible && (
                        <tr
                            onClick={() => setSelectedHex(prev => prev === a.icaoAddress ? null : a.icaoAddress)}
                            style={{
                                cursor: 'pointer',
                                background: isSelected ? 'rgba(255, 107, 53, 0.18)' : undefined
                            }}
                        >
                            <td title={a.country || ''}>
                                <FlagIcon countryCode={a.countryCode} country={a.country} />
                            </td>
                            <td className="hex-cell">
                                {a.icaoAddress ? (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (typeof onJumpToHistory === 'function') onJumpToHistory(a.icaoAddress, a.arrivedAt);
                                        }}
                                        title={`View ${a.icaoAddress} in History${a.ownerOperator ? ' — ' + a.ownerOperator : ''}`}
                                        style={{ color: 'var(--link-color, #4ea3ff)', textDecoration: 'none' }}
                                    >{a.icaoAddress}</a>
                                ) : '-'}
                            </td>
                            <td title={a.aircraftDescription || ''}>{a.registration || '-'}</td>
                            <td><AirlineLogo airlineCode={a.airlineCode} airlineName={a.airline || a.airlineCode} /></td>
                            <td>{a.callsign || '-'}</td>
                            <td>{a.iataFlightNumber || '-'}</td>
                            <td>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    {a.aircraftType || '-'}
                                    {a.aircraftType && <AircraftTypeIcon aircraftType={a.aircraftType} size={14} />}
                                    {a.military && (
                                        <span className="heavy-badge" title="Military aircraft" style={{ background: '#8b0000' }}>M</span>
                                    )}
                                </span>
                            </td>
                            <td><GroundStateBadge state={a.groundState} /></td>
                            <td className="numeric">{dep ? dep.pos : ''}</td>
                            <td>{dep && dep.remainingMin != null ? `${Math.round(dep.remainingMin)}m` : ''}</td>
                            <td>{a.assignedGate || '—'}</td>
                            <td title={a.landingConfidence != null && a.landingConfidence < 100
                                ? `Presumed landing (no surface fix): ${a.landingConfidence} % confidence` : undefined}>
                                {a.landingConfidence != null && a.landingConfidence < 100 ? '~' : ''}{formatTime(a.arrivedAt)}
                                {a.landingConfidence != null && a.landingConfidence < 100 && (
                                    <span className="landing-confidence"> {a.landingConfidence} %</span>
                                )}
                            </td>
                            <td>{formatTime(a.lastUpdateAt)}</td>
                            <td className="numeric">{formatDurationMinutes(a.timeOnGroundMinutes)}</td>
                        </tr>
                        )}
                        </React.Fragment>
                        );
                    })}
                    {sorted.length === 0 && !loading && (
                        <tr>
                            <td colSpan={100} style={{ textAlign: 'center', padding: 24, opacity: 0.6 }}>
                                No aircraft currently on the ground.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            </div>
            </div>
        </div>
    );
}
